/**
 * Pure validation helpers for the editor onboarding journey.
 *
 * No React, no DOM. Step definitions import these to decide auto-pass
 * (does the page already satisfy this step on entry?) and validate
 * (has the user satisfied the step now?).
 */

import type { Data } from "@puckeditor/core";
import { getPageTemplate, pageTemplates } from "@staylayer/puck-components";

// ─── Default-heading detection ───────────────────────────────────────────────

const HERO_TYPES = new Set(["Hero", "HeroSplit"]);
const SOCIAL_PROOF_TYPES = new Set([
  "Testimonial",
  "TestimonialCarousel",
  "LogoCloud",
  "QuoteCard",
]);
const CTA_TYPES = new Set(["CTABanner", "Button", "ButtonGroup"]);
const MIN_PERSONALIZED_HEADING_LEN = 3;
const BLANK_BUILD_TEMPLATE_ID = "boutique-lodge-home";
const BLANK_BUILD_FALLBACK_SEQUENCE = [
  "Navbar",
  "Hero",
  "AccommodationGrid",
  "AmenitiesGrid",
  "GallerySection",
  "HostStory",
  "OfferCards",
  "DestinationGuide",
  "InquiryBand",
  "Footer",
] as const;
type BlankBuildComponentType = (typeof BLANK_BUILD_FALLBACK_SEQUENCE)[number];

let defaultHeadingsCache: Set<string> | null = null;
let defaultHeadingsWarned = false;
let blankBuildSequenceCache: BlankBuildComponentType[] | null = null;
let blankBuildSequenceWarned = false;

/**
 * Build the set of "default" hero/heading strings that ship with our
 * starter templates. Used by e3_hero_personalization to detect whether
 * the user actually customized the heading.
 *
 * Lazy + memoized: computed once on first call. Cheap to import but we
 * avoid running it at module load so the editor's cold start isn't hit
 * when the onboarding flag is off.
 *
 * Degrade path: if the template registry yields an empty set, the e3
 * step downgrades to "any non-empty heading >= 3 chars". We log this
 * once so it surfaces in dev.
 */
export function getDefaultHeadings(): Set<string> {
  if (defaultHeadingsCache) return defaultHeadingsCache;
  const set = new Set<string>();
  for (const tpl of pageTemplates) {
    for (const node of tpl.puckData.content ?? []) {
      const heading = readHeading(node.props);
      if (heading) set.add(heading);
    }
  }
  defaultHeadingsCache = set;
  if (set.size === 0 && !defaultHeadingsWarned) {
    defaultHeadingsWarned = true;
    // eslint-disable-next-line no-console
    console.warn(
      "[onboarding] DEFAULT_HEADINGS came up empty; e3 falls back to length check.",
    );
  }
  return set;
}

export function getBlankBuildSequence(): ReadonlyArray<BlankBuildComponentType> {
  if (blankBuildSequenceCache) return blankBuildSequenceCache;

  const template = getPageTemplate(BLANK_BUILD_TEMPLATE_ID);
  const templateTypes = (template?.puckData.content ?? [])
    .map((node) => (typeof node?.type === "string" ? node.type : null))
    .filter(
      (type): type is BlankBuildComponentType =>
        type !== null &&
        BLANK_BUILD_FALLBACK_SEQUENCE.includes(type as BlankBuildComponentType),
    );

  if (templateTypes.length === BLANK_BUILD_FALLBACK_SEQUENCE.length) {
    blankBuildSequenceCache = [...templateTypes];
    return blankBuildSequenceCache;
  }

  if (!blankBuildSequenceWarned) {
    blankBuildSequenceWarned = true;
    // eslint-disable-next-line no-console
    console.warn(
      "[onboarding] blank build sequence fell back to the hard-coded hospitality order.",
    );
  }

  blankBuildSequenceCache = [...BLANK_BUILD_FALLBACK_SEQUENCE];
  return blankBuildSequenceCache;
}

function readHeading(props: unknown): string | null {
  if (!props || typeof props !== "object") return null;
  const obj = props as Record<string, unknown>;
  const candidates = [obj.heading, obj.title];
  for (const c of candidates) {
    if (typeof c === "string" && c.trim().length > 0) {
      return c.trim();
    }
  }
  return null;
}

// ─── Content predicates ──────────────────────────────────────────────────────

function contentNodes(data: Data | null): Array<{
  type: string;
  props: Record<string, unknown>;
}> {
  if (!data || !Array.isArray(data.content)) return [];
  return data.content
    .filter(
      (n): n is { type: string; props: Record<string, unknown> } =>
        n != null &&
        typeof n === "object" &&
        typeof (n as { type?: unknown }).type === "string",
    )
    .map((n) => ({
      type: n.type,
      props: ((n as { props?: unknown }).props ?? {}) as Record<
        string,
        unknown
      >,
    }));
}

function hasNodeOfType(data: Data | null, types: Set<string>): boolean {
  return contentNodes(data).some((n) => types.has(n.type));
}

function firstHeroHeading(data: Data | null): string | null {
  for (const node of contentNodes(data)) {
    if (HERO_TYPES.has(node.type)) {
      return readHeading(node.props);
    }
  }
  return null;
}

function blankBuildProgress(data: Data | null): number {
  const actualTypes = contentNodes(data).map((node) => node.type);
  const expectedTypes = getBlankBuildSequence();

  let actualIndex = 0;
  let expectedIndex = 0;

  while (
    actualIndex < actualTypes.length &&
    expectedIndex < expectedTypes.length
  ) {
    if (actualTypes[actualIndex] === expectedTypes[expectedIndex]) {
      expectedIndex += 1;
    }
    actualIndex += 1;
  }

  return expectedIndex;
}

// ─── Step-level helpers ──────────────────────────────────────────────────────

export const validators = {
  blankBuildSequenceSatisfied(data: Data | null, throughStep: number): boolean {
    if (throughStep <= 0) return true;
    return blankBuildProgress(data) >= throughStep;
  },
  hasHero(data: Data | null): boolean {
    return hasNodeOfType(data, HERO_TYPES);
  },
  hasSocialProof(data: Data | null): boolean {
    return hasNodeOfType(data, SOCIAL_PROOF_TYPES);
  },
  hasCta(data: Data | null): boolean {
    return hasNodeOfType(data, CTA_TYPES);
  },
  heroHeadingPersonalized(data: Data | null): boolean {
    const heading = firstHeroHeading(data);
    if (!heading) return false;
    if (heading.length < MIN_PERSONALIZED_HEADING_LEN) return false;
    const defaults = getDefaultHeadings();
    if (defaults.size === 0) return true; // degrade path
    return !defaults.has(heading);
  },
  /**
   * SEO basics are "reviewed" once both the meta title and description
   * have a reasonable amount of text. Numbers chosen to match the
   * shortest acceptable lengths for search snippets — they're a hint,
   * not a hard SEO contract.
   */
  seoBasicsCovered(seoTitle: string, seoDescription: string): boolean {
    return seoTitle.trim().length >= 10 && seoDescription.trim().length >= 50;
  },
} as const;

// Test-only resets — never called in app code.
export function __resetValidationCache(): void {
  defaultHeadingsCache = null;
  defaultHeadingsWarned = false;
  blankBuildSequenceCache = null;
  blankBuildSequenceWarned = false;
}
